import React, { useEffect, useMemo, useState } from "react";
import {
    ProductCategoryType,
    ProductDataType,
    ProductTaxCodeType,
    ProductType
} from "../../../../types";
import ProductDetailGallery from "./ProductDetailGallery";
import Button from "../../../shared/Button";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store";
import { addToCart } from "../../../../store/order";
import { graphql, navigate, useStaticQuery } from "gatsby";
import OrderInTheNext from "../../../ecommerce/OrderInTheNext";
import { getPriceToShow } from "../../../../utils/currency";
import ecommerceApi from "../../../../services/ecommerceApi";
import EcommerceApi from "../../../../services/ecommerceApi";

import EditorJsRenderer from "../../../shared/EditorJSRender";
import RecommendedProduct from "../../../ecommerce/RecommendedProduct";
import StockIndicator from "../../../ecommerce/StockIndicator";
import QuantityModifier from "../../../ecommerce/QuantityModifier";
import { ProductJsonLd } from "gatsby-plugin-next-seo";
import { useSalesChannel } from "../../../../context/SalesChannelContext";
import Spinner from "../../../shared/Spinner";
import { GatsbyImage, IGatsbyImageData, getImage } from "gatsby-plugin-image";
import ProductDetailAccreditations from "./Accreditations";
import { extractScCurrValueFromProductAttr } from "../../../../utils/string";
import { constructProductMetricHtml } from "../../../../utils/html";

export interface ProductDetailProps {
    defaultProduct: ProductType;
    slug: string;
}

export default function ProductDetail({
    defaultProduct,
    slug
}: ProductDetailProps) {
    const dispatch = useDispatch<AppDispatch>();

    const salesChannel = useSalesChannel();

    const order = useSelector((state: RootState) => state.order.order);
    const currencyCode = useSelector(
        (state: RootState) => state.currencyCode.selectedCurrencyCode?.currencyCode
    );

    const [selectedProduct, setSelectedProduct] = React.useState<ProductType>(defaultProduct);
    const [taxCodes, setTaxCodes] = useState([]);

    const [quantity, setQuantity] = React.useState(1);

    const [selectedProductData, setSelectedProductData] = React.useState<ProductDataType | null>(
        null
    );
    const [adding, setAdding] = React.useState(false);
    const [buyNowing, setBuyNowing] = React.useState(false);

    let productImage = selectedProduct?.imageFile ?? null;

    if (defaultProduct && !productImage) {
        productImage = defaultProduct.assets ? defaultProduct.assets[0]?.imageFile : null;
    }

    const [imagePreview, setImagePreview] = React.useState<Partial<
        ProductType["assets"][0]
    > | null>(productImage);

    const handleAddToCartClick = async () => {
        setAdding(true);
        const orderId = order ? order.id : null;
        const params = {
            orderId: orderId,
            skuId: selectedProduct.id,
            quantity,
            salesChannel
        };
        await dispatch(addToCart(params));
        setAdding(false);
    };

    const handleBuyNowClick = async () => {
        setBuyNowing(true);
        const orderId = order ? order.id : null;
        const params = {
            orderId: orderId,
            skuId: selectedProduct.id,
            quantity,
            salesChannel
        };
        await dispatch(addToCart(params));
        navigate("/cart");
    };

    const loadProductData = async () => {
        if (selectedProduct) {
            const productId = selectedProduct.product || selectedProduct.id;

            const [product, taxCodes] = await Promise.all([
                ecommerceApi.getProductData(productId),
                ecommerceApi.getProductTaxCodesData(productId)
            ]);

            setTaxCodes(taxCodes);
            setSelectedProductData(product || null);
        }
    };

    const handleProductClick = (product: ProductType) => (e: any) => {
        let childSlug = (product.SKUName || "").toLowerCase();
        childSlug = childSlug.replace(/ /g, "-");
        childSlug = childSlug.replace("---", "-");
        navigate(`${slug}/${childSlug}`);
    };

    const isMobileView = useMemo(() => window.matchMedia("(max-width: 768px)").matches, []);

    useEffect(() => {
        loadProductData();
    }, [selectedProduct, isMobileView]);

    const pricing = useMemo(() => {
        if (selectedProductData) {
            const listPrice = getPriceToShow(
                selectedProductData.listPrice,
                salesChannel,
                currencyCode,
                undefined,
                selectedProduct || undefined
            );
            return {
                price: getPriceToShow(
                    selectedProductData.currentPrice,
                    salesChannel,
                    currencyCode,
                    undefined,
                    selectedProduct || undefined
                ),
                listPrice: selectedProductData?.listPrice || 0 ? listPrice : null
            };
        }
        return null;
    }, [selectedProductData, taxCodes, currencyCode, salesChannel.metadata]);

    const selectedSchema = useMemo(() => {
        if (selectedProduct?.schema) {
            try {
                const parsed = JSON.parse(selectedProduct.schema);
                // if not array
                if (!Array.isArray(parsed)) {
                    return parsed;
                }
            } catch (e) {
                console.warn(e);
            }
        }
        return null;
    }, [selectedProduct]);

    let shortDescription = useMemo(
        () =>
            extractScCurrValueFromProductAttr(
                selectedProduct.shortDescription,
                salesChannel?.id,
                "shortDescription"
            ),
        [salesChannel?.id, selectedProduct.shortDescription]
    );

    let productMetricHtml = constructProductMetricHtml(
        selectedProduct?.productPricingMetrics,
        selectedProduct
    );

    return (
        <>
            <section className="py-10 bg-gray-100 overflow-hidden">
                <div className="container max-w-screen-xl mx-auto px-3">
                    <div className="flex flex-wrap items-center -mx-5">
                        <div className="w-full md:w-5/12 px-5">
                            <ProductDetailGallery
                                imagePreview={imagePreview}
                                selectedProduct={selectedProduct}
                                setImagePreview={setImagePreview}
                            />
                        </div>

                        <div className="md:w-1/12 px-5"></div>

                        <div className="w-full md:w-6/12 px-5">
                            <div className="mb-4">
                                <h1 className="text-4xl text-extrabold mb-3">
                                    {defaultProduct.SKUName}
                                </h1>
                                <div className="flex items-center -mx-3">
                                    <div className="mx-3">
                                        <span className="text-3xl h-[35px] flex items-center font-bold text-center md:text-left">
                                            {selectedProductData == null ? (
                                                <Spinner className="px-5" height="35px" />
                                            ) : (
                                                <>
                                                    {pricing?.listPrice && (
                                                        <span className="text-gray-500 font-normal line-through mr-2">
                                                            {pricing?.listPrice}
                                                        </span>
                                                    )}
                                                    {pricing?.price}
                                                </>
                                            )}
                                        </span>
                                    </div>

                                    {selectedProductData &&
                                        selectedProductData?.stock &&
                                        selectedProductData?.stock?.type &&
                                        selectedProductData?.stock?.type !==
                                        "STOCK_TYPE_NOT_SET" && (
                                            <div className="mx-3">
                                                <StockIndicator
                                                    stock={selectedProductData?.stock}
                                                    loading={selectedProductData == null}
                                                />
                                            </div>
                                        )}
                                </div>

                                {selectedProduct?.productMetricHtml && (
                                    <div
                                        className="mt-5 text-xl -mx-2"
                                        dangerouslySetInnerHTML={{
                                            __html: productMetricHtml as string
                                        }}
                                    ></div>
                                )}

                                {selectedProduct.shortDescription && (
                                    <div className="pt-5 text-xl">{shortDescription}</div>
                                )}
                            </div>
                            <section className="mb-6">
                                <h2 className="block mb-2 font-bold text-secondary text-sm uppercase">
                                    Quantity
                                </h2>
                                <QuantityModifier
                                    quantity={quantity}
                                    handleQuantityChange={setQuantity}
                                />
                            </section>
                            <section className="mb-6">
                                <OrderInTheNext justify="start" align="center" />
                            </section>
                            <div className="flex flex-wrap -mx-2 mt-1">
                                <div className="w-full md:w-1/2 px-2">
                                    <Button
                                        padding="py-3 px-6"
                                        className="w-full mb-2"
                                        loader={adding}
                                        onClick={handleAddToCartClick}
                                        disabled={
                                            selectedProductData?.stock?.type == null ||
                                            selectedProductData?.stock?.type == "outOfStock"
                                        }
                                    >
                                        Add to Cart
                                    </Button>
                                    <Button
                                        type="secondary"
                                        padding="py-3 px-6"
                                        className="w-full"
                                        loader={buyNowing}
                                        onClick={handleBuyNowClick}
                                        disabled={
                                            selectedProductData?.stock?.type == null ||
                                            selectedProductData?.stock?.type == "outOfStock"
                                        }
                                    >
                                        Buy Now
                                    </Button>
                                </div>
                            </div>

                            <div className="mt-5">
                                <ProductDetailAccreditations
                                    accreditations={selectedProduct.accreditations}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>
            <section className="bg-white py-10">
                <div className="container max-w-screen-xl mx-auto px-3">
                    <div className="text-xl">
                        <ProductDetailHeading>Product Description</ProductDetailHeading>
                        <EditorJsRenderer content={selectedProduct.fullDescription} />
                    </div>
                    <div></div>


                    <ProductJsonLd
                        name={selectedProduct?.SKUName}
                        brand={selectedSchema?.brand}
                        images={[selectedProduct.imageFile?.publicURL || selectedSchema?.image]}
                        gtin={selectedSchema?.gtin}
                        offers={pricing ? {
                            price: pricing.price,
                            priceCurrency: currencyCode || "GBP"
                        } : undefined}
                        mpn={selectedSchema?.mpn}
                        reviews={(selectedProduct?.reviews ?? []).map(rev => {
                            return {
                                author: rev.author ?? rev.author,
                                datePublished: rev.datePublished,
                                reviewBody: rev.body,
                                name: rev.title,
                                reviewRating: {
                                    bestRating: "5",
                                    ratingValue: rev.ratingValue,
                                    worstRating: "0"
                                }
                            };
                        })}
                    />
                </div>
            </section>
            {selectedProduct &&
                selectedProduct.relatedProducts &&
                selectedProduct.relatedProducts.length > 0 && (
                    <section className="py-10 bg-gray-100">
                        <div className="container max-w-screen-xl mx-auto px-3">
                            <ProductDetailHeading>Related Products</ProductDetailHeading>
                            <div className="flex flex-wrap -mx-3">
                                {selectedProduct.relatedProducts.map(prd => (
                                    <RecommendedProduct key={prd.id} product={prd} />
                                ))}
                            </div>
                        </div>
                    </section>
                )}
        </>
    );
}

const ProductDetailHeading = ({ children }: { children: React.ReactNode }) => (
    <h2 className="text-3xl uppercase text-extrabold mb-5">{children}</h2>
);
